import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { FaFacebookF, FaYoutube, FaTwitter, FaLinkedinIn, FaPhoneAlt } from 'react-icons/fa';
import { MdEmail, MdLocationOn } from 'react-icons/md';
import contactBackground from '../assets/images/background-purple.jpg';
import greenCube from '../assets/images/green-cube.png';
import { content, getLangFromPath } from '../i18n';

function Contact() {
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const t = content[lang].contact;

  const recaptchaRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    subject: '',
    message: ''
  });

  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [status, setStatus] = useState({
    loading: false,
    error: '',
    success: ''
  });

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    setStatus({
      loading: false,
      error: '',
      success: ''
    });

    if (!formData.fullName || !formData.email || !formData.subject || !formData.message) {
      setStatus({
        loading: false,
        error: lang === 'gr' ? 'Συμπλήρωσε όλα τα πεδία.' : 'Please fill in all fields.',
        success: ''
      });
      return;
    }

    if (!recaptchaToken) {
      setStatus({
        loading: false,
        error: lang === 'gr' ? 'Συμπλήρωσε το captcha.' : 'Please complete the captcha.',
        success: ''
      });
      return;
    }

    try {
      setStatus({
        loading: true,
        error: '',
        success: ''
      });

      const response = await fetch('https://greenyourfreight.eu/api/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          recaptchaToken
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Something went wrong.');
      }

      setFormData({
        fullName: '',
        email: '',
        subject: '',
        message: ''
      });

      setRecaptchaToken('');

      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      setStatus({
        loading: false,
        error: '',
        success: lang === 'gr'
          ? 'Το μήνυμά σου στάλθηκε επιτυχώς.'
          : 'Your message was sent successfully.'
      });
    } catch (error) {
      setStatus({
        loading: false,
        error: error.message || (lang === 'gr' ? 'Κάτι πήγε στραβά.' : 'Something went wrong.'),
        success: ''
      });

      setRecaptchaToken('');

      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  }

  return (
    <section
      className="contact-page"
      style={{
        backgroundImage: `linear-gradient(rgba(29, 10, 97, 0.9), rgba(29, 10, 97, 0.9)), url(${contactBackground})`
      }}
    >
      <div className="container contact-grid">
        <div className="contact-info-panel">
          <div className="section-title light-title">
            <img src={greenCube} alt="" />
            <div>
              <span>{t.label}</span>
              <h1>{t.title}</h1>
            </div>
          </div>

          <p>{t.intro}</p>

          <div className="contact-lines">
            <a href="tel:+302100000000">
              <FaPhoneAlt />
              <span>+30 210 0140 312</span>
            </a>

            <a href="mailto:support@greenyourfreight.eu">
              <MdEmail />
              <span>support@greenyourfreight.eu</span>
            </a>

            <div>
              <MdLocationOn />
              <span>{t.actualAddress}</span>
            </div>
          </div>

          <div className="socials large">
            <a href="https://www.facebook.com/profile.php?id=61553672166383" target="_blank" rel="noreferrer" aria-label="Facebook">
              <FaFacebookF />
            </a>
            <a href="https://x.com/GYF_project" target="_blank" rel="noreferrer" aria-label="Instagram">
              <FaTwitter />
            </a>
            <a href="https://www.linkedin.com/company/life-green-your-freight/?viewAsMember=true" target="_blank" rel="noreferrer" aria-label="LinkedIn">
              <FaLinkedinIn />
            </a>
            <a href="https://www.youtube.com/@LIFEGreenYourFreight" target="_blank" rel="noreferrer" aria-label="LinkedIn">
              <FaYoutube/>
            </a>
          </div>
        </div>

        <div className="contact-form-card">
          <h2>{t.formTitle}</h2>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <input
                type="text"
                name="fullName"
                placeholder={t.fullName}
                value={formData.fullName}
                onChange={handleChange}
                required
              />
              <input
                type="email"
                name="email"
                placeholder={t.email}
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row single">
              <input
                type="text"
                name="subject"
                placeholder={t.subject}
                value={formData.subject}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row single">
              <textarea
                rows="6"
                name="message"
                placeholder={t.message}
                value={formData.message}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row single">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={token => setRecaptchaToken(token || '')}
              />
            </div>

            {status.error && (
              <p className="form-status error">{status.error}</p>
            )}

            {status.success && (
              <p className="form-status success">{status.success}</p>
            )}

            <button type="submit" className="primary-btn submit-btn" disabled={status.loading}>
              {status.loading
                ? (lang === 'gr' ? 'Αποστολή...' : 'Sending...')
                : t.send}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

export default Contact;